import {
  Box,
  FormControlLabel,
  Grid,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import { useRouter } from 'next/router'
import { AssayTypeItem } from '@src/types/graphql-types'
import { checkAssayTypeDisabled } from '@src/routes/Configurators/products/Milliplex/utils'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'

const useStyles = makeStyles((theme: Theme) => {
  return {
    assaysHeaderStyle: {
      margin: theme.spacing(4, 0, 4, 0),
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
    },
    selectAssayHeaderStyle: {
      margin: theme.spacing(0, 0, 4, 0),
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
    },
    radioContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    radioControlStyle: {
      marginRight: theme.spacing(22),
      '& .MuiTypography-body1': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightMedium,
      },
      '& .Mui-disabled': {
        cursor: 'no-drop',
        color: theme.palette.text.primary,
      },
      '& .MuiButtonBase-root': {
        paddingBottom: theme.typography.pxToRem(4),
      },
    },
    assayTypeDescription: {
      marginLeft: theme.spacing(8),
      fontSize: theme.typography.pxToRem(14),
    },
  }
})

export interface AssaysProps {
  assayTypeList: AssayTypeItem[]
  loading: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
}

const Assays: React.FC<AssaysProps> = ({
  assayTypeList,
  loading,
  setDisplayPageError,
}) => {
  const classes = useStyles()
  const formik = useFormikContext<MilliplexFormValues>()
  const router = useRouter()
  const assayDescription = {
    Qualified:
      'Highly Verified and reproducible MILLIPLEX® assays containing critical reagents for precise and accurate performance.',
    Screening:
      'Fit-for-purpose analyte profiling MILLIPLEX® assays to detect upto 115 proteins in biofluids and tissue/cell culture samples.',
  }
  const onChange = (e) => {
    const value = e.target.value
    formik.setValues({
      ...formik.values,
      assay: value,
      plateType: '',
      instrumentType: [],
      analytes: [],
    })
    formik.setFieldTouched('analytes', false)
    setDisplayPageError(false)
    router.replace(
      {
        pathname: router.pathname,
        query: {
          activeLink: '',
          country: router.query?.country,
          language: router.query?.language,
          researchAreas: router.query?.researchAreas,
          species: router.query?.species,
          assay: value,
        },
      },
      undefined,
      { shallow: true }
    )
  }
  const checkDisabled = (assay: AssayTypeItem) => {
    const species = router?.query?.species || ''
    const researchArea = Array.isArray(router.query.researchAreas)
      ? router.query.researchAreas
      : router.query.researchAreas
        ? [router.query.researchAreas]
        : []
    return checkAssayTypeDisabled(assay, species, researchArea)
  }

  return (
    <div>
      <Typography variant="subtitle2" className={classes.assaysHeaderStyle}>
        <FormattedMessage id="ASSAYS" defaultMessage="Assays" />
      </Typography>
      <Typography
        variant="subtitle2"
        className={classes.selectAssayHeaderStyle}
      >
        <FormattedMessage
          id="SELECT_ASSAY_TYPE"
          defaultMessage="Select Assay Type"
        />
      </Typography>

      {loading && assayTypeList?.length === 0 && (
        <Grid container spacing={2}>
          {Array(3)
            .fill(undefined)
            .map((_, index) => (
              <Grid item xs={4} key={index}>
                <Skeleton animation="wave" height={40} width="100%" />
              </Grid>
            ))}
        </Grid>
      )}
      <Grid container>
        <Grid item xs={12} className={classes.radioContainer}>
          <RadioGroup
            aria-label="assayTypePlateType"
            name="assayTypePlateType"
            row
          >
            {assayTypeList?.map(
              (assay) =>
                assay && (
                  <Grid item container key={assay.assayType}>
                    {' '}
                    <Box
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: '16px',
                      }}
                    >
                      <FormControlLabel
                        className={classes.radioControlStyle}
                        control={<StyledRadio isConfiguratorsRedesign="true" />}
                        label={assay.assayType}
                        value={assay.assayType}
                        disabled={checkDisabled(assay)}
                        onChange={onChange}
                        checked={assay.assayType === router.query?.assay}
                        key={assay.assayType}
                      />
                      {assay.assayType === 'Qualified' ||
                      assay.assayType === 'Screening' ? (
                        <Box className={classes.assayTypeDescription}>
                          {' '}
                          <FormattedMessage
                            id={`ASSAY_DESCRIPTION-${assay.assayType}`}
                            defaultMessage={assayDescription[assay.assayType]}
                          />
                        </Box>
                      ) : (
                        ''
                      )}
                    </Box>
                  </Grid>
                )
            )}
          </RadioGroup>
        </Grid>
        <ValidationErrors
          errors={
            formik.touched.assay && formik.errors.assay && !formik.values.assay
              ? [formik.errors.assay]
              : []
          }
          configuratorsRedesign
        />
      </Grid>
    </div>
  )
}

export default Assays
